/* eslint-disable sonarjs/cognitive-complexity */
'use client';
import { cn } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import {
  ActionIcon,
  Box,
  type BoxProps,
  CopyButton,
  Group,
  rem,
  Space,
  Stack,
  Title,
  Tooltip,
} from '@mantine/core';
import { ArrowLeft, RouteIcon, Check, Copy } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useState, useMemo, type PropsWithChildren } from 'react';
import classes from './booking-layout.module.css';
import { publicEnv } from '@/config/public-env.config.mjs';
import { ROUTES } from '@/config/router';
import SaveDraftSection from '@/features/booking/components/save-draft/save-draft-section';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

const RO_PATH = [
  {
    address:
      '84 Đường An Dương Vương, Phường 09, Quận 05, Hồ Chí Minh, Việt Nam',
    name: 'CHỊ LOAN',
    mobile: '0909634751',
    remarks: 'LÔ H ',
    tracking_number: 'SO_0001',
  },
  {
    address: '351 Đường Phan Xích Long, Phường 01, Quận Phú Nhuận',
    name: 'CHỊ TÂM ĐAN ',
    mobile: '0987235325',
    cod: 420000,
    tracking_number: 'SO_0002',
    lat: 10.834855,
    lng: 106.781932,
  },
  {
    address: '83 Đường Số 5, Phường 17, Gò Vấp',
    name: 'FB PHUC HOANG',
    mobile: '0907935292',
    cod: 245000,
    tracking_number: 'SO_0003',
  },
  {
    address: '26 Út Tịch, Phường 04, Quận Tân Bình',
    name: 'CHỊ XUÂN',
    mobile: '0908158017',
    tracking_number: 'SO_0004',
  },
  {
    address: '290 Nguyễn Thị Nhỏ, Phường 06, Quận 11',
    name: 'CHỊ TÂM ĐAN ',
    mobile: '0987235325',
    cod: 420000,
    tracking_number: 'SO_0005',
  },
  {
    address: '16 Tân Khai, Phường 4, Tân Bình, Ho Chi Minh City',
    short_address: '16 Tân Khai',
    name: 'FB QUANG',
    mobile: '0907935292',
    cod: 245000,
    tracking_number: 'SO_0006',
  },
  {
    address: '668 Đường Âu Cơ, Phường 14, Quận Tân Bình',
    name: 'CHỊ THẢO',
    mobile: '0908158017',
    tracking_number: 'SO_0007',
  },
];

const RO_INITIAL_DATA = {
  path: RO_PATH,
  service_ids: ['BIKE'],
  request_ids: ['TIP', 'COD'],
  promo_code: 'AHAKIOT',
  payment_method: [
    {
      payment: 'cash',
    },
    {
      payment: 'balance',
    },
  ],
  default_payment_method: {
    public_method: 'BALANCE',
  },
};

type Props = BoxProps &
  PropsWithChildren & {
    title: string;
  };

const BookingHeading = ({ title, children, className, ...props }: Props) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const isTruckPage = pathname.includes(ROUTES.truck);
  const isHouseMovingPage = pathname.includes(ROUTES.houseMoving);
  const order = useBookingStore((state) => state.orderDetails);
  const bgStyle = useMemo(() => {
    const baseUrl = publicEnv.NEXT_PUBLIC_DOMAIN === 'localhost' ? '' : '/v3';
    const style = {
      backgroundSize: 'cover',
      backgroundPosition: 'top',
      backgroundRepeat: 'no-repeat',
    };

    if (isTruckPage) {
      return {
        ...style,
        backgroundImage: `url('${baseUrl}/static/icons/bg-header-truck.svg')`,
      };
    }
    if (isHouseMovingPage) {
      return {
        ...style,
        backgroundImage: `url('${baseUrl}/static/icons/bg-header-hm.svg')`,
      };
    }
    return {};
  }, [isTruckPage, isHouseMovingPage]);

  // const [shouldInitRO, setShouldInitRO] = useState(false);
  const [iframeReady, setIframeReady] = useState(false);
  const auth = useAuth();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'RO_IFRAME_READY') {
        console.log('[Client Parent] ✅ Iframe is ready!');
        setIframeReady(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleInitRO = () => {
    if (!window.RO) return;

    if (iframeReady) {
      window.RO.RPCs.togglePanel();
      return;
    }

    console.log('Initializing RO...');

    window.RO.init({
      theme: 'light',
      token: auth.session?.token,
      enableAutoFocus: true,
      enableSounds: true,
      initialData: RO_INITIAL_DATA,
    });
  };

  return (
    <Box
      className={cn(
        classes.detailsHeader,
        'overflow-x-hidden shadow-shadow8',
        className
      )}
      {...props}
    >
      <Script
        // src="http://localhost:3001/main.0.0.1.js"
        src={`${process.env.NEXT_PUBLIC_RO_NEXT_EMBED_URL}?v=${Date.now()}`}
      />

      {isMobile ? (
        <Group
          justify={'space-between'}
          w="100%"
          px="md"
          mih={rem(45)}
          bg={isTruckPage || isHouseMovingPage ? undefined : 'white'}
          style={{
            zIndex: 10,
            flexDirection: 'row',
            borderBottom: 'none',
            ...bgStyle,
          }}
        >
          <ActionIcon
            size={rem(24)}
            onClick={() => {
              router.back();
            }}
            variant="transparent"
          >
            <ArrowLeft
              width={24}
              height={24}
              color={isTruckPage ? 'white' : 'black'}
            />
          </ActionIcon>
          <Title
            order={5}
            c={isTruckPage ? 'white' : 'neutral.7'}
            fz={rem(18)}
            lh={rem(32)}
            fw={700}
          >
            {title}
          </Title>
          <Space w={rem(32)} />
        </Group>
      ) : null}
      <Stack gap={isMobile ? 0 : 12} p={isMobile ? 0 : 'md'} style={bgStyle}>
        {!isMobile ? (
          <Group>
            <Title
              order={1}
              c={isTruckPage || isHouseMovingPage ? 'white' : 'neutral.7'}
              fz={rem(24)}
              lh={rem(32)}
              fw={700}
            >
              {title}{' '}
              {order?._id && (
                <CopyButton value={order?._id} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? 'Đã sao chép' : 'Sao chép'}
                      withArrow
                      position="right"
                    >
                      <ActionIcon
                        color={copied ? 'green.6' : 'primary.6'}
                        variant="transparent"
                        onClick={copy}
                      >
                        {copied ? <Check size={18} /> : <Copy size={18} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              )}{' '}
            </Title>

            {!process.env.NEXT_PUBLIC_GTM_KEY ? (
              <ActionIcon
                size={rem(24)}
                variant="white"
                ml="auto"
                onClick={handleInitRO}
              >
                <RouteIcon size={18} />
              </ActionIcon>
            ) : null}
          </Group>
        ) : null}
        {children}
      </Stack>
      <SaveDraftSection />
    </Box>
  );
};

export default BookingHeading;
